/**
 * Client error telemetry — transport + local queue.
 *
 * Spec: docs/CLIENT_ERROR_TELEMETRY_PLAN.md (steps 2-5). Server: POST /api/client-errors
 * (promptus-api ClientErrorHelper). This module reports "the user hit a wall" events —
 * handled failures, silent no-ops, false successes, stuck spinners — not only thrown
 * exceptions.
 *
 * DESIGN CONSTRAINTS (each one is load-bearing — read before changing):
 *
 *  - METADATA ONLY. Never prompt text, base64/imageData/b64data, file names, tokenised URLs,
 *    free-text error messages, or DOM state. Every value is normalised to a short
 *    identifier-like string before it is queued (see `toIdentifier`). The server re-validates
 *    and silently drops anything else, but the client must not rely on that.
 *  - PATTERN #62. The reporter must never become a large client-side payload. The queue is
 *    hard-capped by event count AND serialized bytes, every event is size-capped, and a
 *    request body stays under the 64 KB keepalive budget.
 *  - NO DEPENDENCY ON THE THINGS THAT FAIL. This module imports nothing from db.js,
 *    api-helper.js, Amplitude or Swal, and is NOT gated by window.__SAFE_MODE__ — it has to
 *    keep working exactly when the rest of the app is at its worst. Its only storage is
 *    localStorage (with an in-memory fallback if localStorage throws).
 *  - AUTH. `Authorization: Bearer <the user's own API key>`, header only. The client ships
 *    no shared/anonymous app credential at all, and never puts a token in the body or
 *    URL. Events raised while logged out stay in the queue and are sent after login, or age
 *    out after EVENT_TTL_MS.
 *  - UNLOAD. fetch(..., {keepalive:true}) with the Authorization header — NOT sendBeacon,
 *    which cannot carry the header and would always be refused.
 *  - RESPONSES. 200 = done. 400/401/403/413 = drop the batch, never retry. 429 (honour
 *    Retry-After) / 503 / network failure / anything else = keep and back off. Every event
 *    has a stable UUID `eventId`, so a re-send is de-duplicated server-side.
 *  - OPT-OUT. On by default. The Settings toggle writes OPT_OUT_KEY; when set, nothing is
 *    recorded and the queue is cleared. FEATURE_FLAGS.ENABLE_CLIENT_ERROR_TELEMETRY is the
 *    build-time kill switch.
 */
import { FEATURE_FLAGS } from '../config/featureFlags';
import { isApiKeyAccountSession } from '../utils/apiKeyAccountSession';

/* ── Limits ─────────────────────────────────────────────────────────────── */

export const QUEUE_KEY = 'client_telemetry_queue_v1';
export const OPT_OUT_KEY = 'client_telemetry_disabled';
const SESSION_KEY = 'client_telemetry_session_id';
const PERSISTED_MARK_KEY = 'client_telemetry_persisted_seen';

export const LIMITS = {
    MAX_QUEUE_EVENTS: 100,
    MAX_QUEUE_BYTES: 48 * 1024,
    MAX_EVENT_BYTES: 2 * 1024,
    MAX_EVENTS_PER_REQUEST: 50,
    // Server cap is 64 KB; keepalive's in-flight budget is also 64 KB for the whole page.
    MAX_BODY_BYTES: 60 * 1024,
    // Server rejects client timestamps older than 7 days; stay well inside it.
    EVENT_TTL_MS: 72 * 60 * 60 * 1000,
    MAX_SEND_ATTEMPTS: 8,
    BACKOFF_BASE_MS: 5 * 1000,
    BACKOFF_MAX_MS: 10 * 60 * 1000,
    FLUSH_DELAY_MS: 5 * 1000,
    FLUSH_INTERVAL_MS: 60 * 1000,
    // Sampling windows (docs/CLIENT_ERROR_TELEMETRY_PLAN.md "Sampling").
    DEGRADED_DEDUPE_WINDOW_MS: 10 * 60 * 1000,
    BLOCKED_COALESCE_WINDOW_MS: 5 * 1000,
    MAX_DEDUPE_KEYS: 200,
    MAX_DETAIL_KEYS: 12,
    MAX_COMPONENT_NAMES: 20,
};

export const CATEGORIES = ['blocked_explained', 'blocked_silent', 'false_success', 'stuck_state', 'degraded_continued'];

// Plan: send EVERYTHING for false_success and stuck_state; they are also the last to be
// evicted when the queue is full.
const HIGH_PRIORITY = { false_success: true, stuck_state: true };

// Keys accepted inside the per-event `detail` object (mirrors the server whitelist).
export const DETAIL_KEYS = [
    'swalTitle', 'swalIcon', 'sourceFile', 'sourceLine', 'jobUid', 'action', 'loginMethod',
    'key', 'page', 'mediaType', 'dbErrorPresent', 'validatorName', 'table', 'uid', 'attempts',
    'path', 'componentNames',
];

/* ── Injectable environment (tests replace these) ───────────────────────── */

const env = {
    now: () => Date.now(),
    fetch: (...args) => (typeof fetch === 'function' ? fetch(...args) : Promise.reject(new Error('no_fetch'))),
    storage: () => (typeof window !== 'undefined' ? window.localStorage : null),
    session: () => (typeof window !== 'undefined' ? window.sessionStorage : null),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
    random: () => Math.random(),
    idGenerator: null, // tests inject a deterministic generator
    apiBase: null,
};

/** Test/boot hook: override environment pieces. */
export const configureTelemetry = (overrides = {}) => {
    Object.assign(env, overrides);
};

/* ── Small pure helpers (exported for tests) ────────────────────────────── */

export const utf8Length = (str) => {
    let bytes = 0;
    for (let i = 0; i < str.length; i++) {
        const c = str.charCodeAt(i);
        if (c < 0x80) bytes += 1;
        else if (c < 0x800) bytes += 2;
        else if (c >= 0xd800 && c <= 0xdbff) { bytes += 4; i++; }
        else bytes += 3;
    }
    return bytes;
};

// The server de-duplicates on eventId, so a missing or repeated id would silently merge or
// drop events. Every source is validated, and a degenerate one falls through to the next.
const VALID_ID = /^[A-Za-z0-9_-]{8,64}$/;

export const generateEventId = () => {
    try {
        if (typeof env.idGenerator === 'function') {
            const injected = env.idGenerator();
            if (typeof injected === 'string' && VALID_ID.test(injected)) return injected;
        }
        const c = typeof crypto !== 'undefined' ? crypto : null;
        if (c && typeof c.randomUUID === 'function') {
            const id = c.randomUUID();
            if (typeof id === 'string' && VALID_ID.test(id)) return id;
        }
        if (c && typeof c.getRandomValues === 'function') {
            const b = new Uint8Array(16);
            c.getRandomValues(b);
            if (b.every((x) => x === 0)) throw new Error('degenerate_random'); // e.g. a reset mock
            b[6] = (b[6] & 0x0f) | 0x40;
            b[8] = (b[8] & 0x3f) | 0x80;
            const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
            return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
        }
    } catch (e) { /* fall through */ }
    let s = '';
    for (let i = 0; i < 32; i++) s += Math.floor(env.random() * 16).toString(16);
    return `${s.slice(0, 8)}-${s.slice(8, 12)}-4${s.slice(13, 16)}-a${s.slice(17, 20)}-${s.slice(20)}`;
};

const FILE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|avif|svg|mp4|mov|webm|mkv|avi|wav|mp3|flac|ogg|m4a|pdf|docx?|txt|csv|zip|json|prompt|safetensors|ckpt)$/i;

/**
 * Normalise any value into a short identifier-like token, or null.
 * Rejects things that look like content (data URIs, base64 runs, e-mail addresses, URLs,
 * file names) outright rather than trying to "clean" them — a mangled secret is still a
 * secret. Whitespace becomes "_", anything outside the allowed class is removed.
 */
const IDENTIFIER_DISALLOWED = /[^A-Za-z0-9_.:-]/g;

export const toIdentifier = (value, maxLength = 64, disallowed = IDENTIFIER_DISALLOWED) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' || typeof value === 'boolean') value = String(value);
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (!raw || raw.length > 256) return null;
    if (/data:[a-z]+\//i.test(raw)) return null;
    if (/[A-Za-z0-9+/=]{48,}/.test(raw)) return null;              // base64-ish / token-ish run
    if (/[^\s@]+@[^\s@]+\.[A-Za-z]{2,}/.test(raw)) return null;    // e-mail
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && /:\/\/|^(file|blob|data):/i.test(raw)) return null; // URL
    if (/[\\/]/.test(raw)) return null;                            // filesystem / URL paths
    if (FILE_EXT_RE.test(raw)) return null;
    // String.prototype.replace resets a global regex's lastIndex, so sharing it is safe.
    const out = raw.replace(/\s+/g, '_').replace(disallowed, '')
        .replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    if (!out) return null;
    return out.slice(0, maxLength);
};

// modelName may contain "/", "(", ")" and "+" server-side; everything else uses the
// identifier class.
const MODEL_NAME_DISALLOWED = /[^A-Za-z0-9_.:/()+-]/g;
const LABEL_DISALLOWED = /[^A-Za-z0-9_.:[\]-]/g;

export const toModelName = (value) => {
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (!raw || raw.length > 256) return null;
    if (/data:[a-z]+\//i.test(raw) || /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) return null;
    if (/[A-Za-z0-9+/=]{48,}/.test(raw)) return null;
    if (/\\/.test(raw) || raw.startsWith('/') || FILE_EXT_RE.test(raw)) return null; // local paths
    const out = raw.replace(/\s+/g, '_').replace(MODEL_NAME_DISALLOWED, '').replace(/_+/g, '_');
    return out ? out.slice(0, 128) : null;
};

/**
 * Current route, path only: query string and fragment removed, long id-like segments
 * masked. Hash routing (`#/cosyflows/<id>?x=y`) is the normal case in this app.
 */
export const sanitizeRoute = (loc) => {
    try {
        const l = loc || (typeof window !== 'undefined' ? window.location : null);
        if (!l) return null;
        // Only an http(s) pathname is a route. Electron loads the app from file://, where the
        // pathname is a filesystem path that can contain the OS user name.
        const isHttp = typeof l.protocol !== 'string' || /^https?:$/i.test(l.protocol);
        let path = l.hash && l.hash.length > 1 ? l.hash : (isHttp ? (l.pathname || '/') : '/');
        // Drop query string and any nested fragment.
        path = path.replace(/^#/, '').split('?')[0].split('#')[0];
        if (!path.startsWith('/')) path = `/${path}`;
        const segments = path.split('/').map((seg) => {
            if (!seg) return seg;
            let decoded = seg;
            try { decoded = decodeURIComponent(seg); } catch (e) { /* keep raw */ }
            if (!/^[A-Za-z0-9_.:-]+$/.test(decoded)) return ':id';
            if (/^[0-9a-fA-F-]{16,}$/.test(decoded)) return ':id';
            if (decoded.length >= 16 && /[0-9]/.test(decoded) && /[A-Za-z]/.test(decoded)) return ':id';
            if (decoded.length > 48) return ':id';
            return decoded;
        });
        return `#${segments.join('/')}`.slice(0, 128);
    } catch (e) {
        return null;
    }
};

/**
 * Request URL → dotted, id-masked path identifier, e.g.
 * "https://api.promptus.ai/api/generate/output/<uid>/<uid>?x=1" → "api.generate.output.:id.:id".
 * Host, query and fragment are dropped; any segment that is not a short plain word is masked.
 */
export const sanitizeApiPath = (url) => {
    try {
        if (typeof url !== 'string' || !url) return null;
        const path = url.split(/[?#]/)[0].replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
        const segs = path.split('/').filter(Boolean).slice(0, 8).map((seg) => (
            /^[A-Za-z][A-Za-z_-]{0,31}$/.test(seg) ? seg : ':id'
        ));
        if (!segs.length) return null;
        return segs.join('.').slice(0, 64);
    } catch (e) {
        return null;
    }
};

/**
 * Top stack frame → { sourceFile, sourceLine } using only the bundle file BASENAME and
 * line:col — never the full URL (which could carry a host or query).
 * `skip` frames are dropped first (e.g. the wrapper that captured the stack).
 */
export const frameFromStack = (stack, skip = 0) => {
    if (typeof stack !== 'string') return {};
    const frames = [];
    const lines = stack.split('\n');
    for (let i = 0; i < lines.length; i++) {
        const m = lines[i].match(/([^\s()@/\\]+?)(?:\?[^\s():]*)?:(\d+):(\d+)\)?\s*$/);
        if (m) frames.push({ file: m[1], line: `${m[2]}:${m[3]}` });
    }
    const f = frames[skip];
    if (!f) return {};
    return { sourceFile: toIdentifier(f.file, 64), sourceLine: toIdentifier(f.line, 32) };
};

/** React componentStack → up to 20 component names (identifiers only). */
export const componentNamesFromStack = (componentStack) => {
    if (typeof componentStack !== 'string') return [];
    const names = [];
    const lines = componentStack.split('\n');
    for (let i = 0; i < lines.length && names.length < LIMITS.MAX_COMPONENT_NAMES; i++) {
        const m = lines[i].match(/^\s*(?:at|in)\s+([A-Za-z_$][\w$.]*)/);
        if (m) {
            const n = toIdentifier(m[1], 64);
            if (n) names.push(n);
        }
    }
    return names;
};

/** Sanitise the free-form `detail` object down to whitelisted, identifier-like values. */
export const sanitizeDetail = (detail) => {
    if (!detail || typeof detail !== 'object') return null;
    const out = {};
    let count = 0;
    for (let i = 0; i < DETAIL_KEYS.length && count < LIMITS.MAX_DETAIL_KEYS; i++) {
        const k = DETAIL_KEYS[i];
        if (!(k in detail)) continue;
        const v = detail[k];
        if (v === null || v === undefined) continue;
        if (k === 'componentNames') {
            if (!Array.isArray(v)) continue;
            const names = v.map((n) => toIdentifier(n, 64)).filter(Boolean).slice(0, LIMITS.MAX_COMPONENT_NAMES);
            if (names.length) { out[k] = names; count++; }
            continue;
        }
        if (typeof v === 'boolean') { out[k] = v; count++; continue; }
        if (typeof v === 'number') {
            if (Number.isFinite(v)) { out[k] = Math.max(0, Math.min(2147483647, Math.round(v))); count++; }
            continue;
        }
        const s = toIdentifier(v, 64);
        if (s) { out[k] = s; count++; }
    }
    return count ? out : null;
};

const detectPlatform = () => {
    try {
        if (typeof navigator !== 'undefined' && /Electron\//.test(navigator.userAgent || '')) return 'electron';
    } catch (e) { /* ignore */ }
    return 'web';
};

const clientVersion = () => toIdentifier(process.env.REACT_APP_VERSION || '', 32, /[^A-Za-z0-9._+-]/g);

/* ── Opt-out / enablement ───────────────────────────────────────────────── */

let memoryOptOut = false;

export const isTelemetryEnabled = () => {
    if (!FEATURE_FLAGS.ENABLE_CLIENT_ERROR_TELEMETRY) return false;
    try {
        const s = env.storage();
        if (s) return s.getItem(OPT_OUT_KEY) !== 'true';
    } catch (e) { /* fall back to memory */ }
    return !memoryOptOut;
};

/** Settings toggle. Disabling also discards anything already queued. */
export const setTelemetryEnabled = (enabled) => {
    memoryOptOut = !enabled;
    try {
        const s = env.storage();
        if (s) {
            if (enabled) s.removeItem(OPT_OUT_KEY);
            else s.setItem(OPT_OUT_KEY, 'true');
        }
    } catch (e) { /* memory flag still applies */ }
    if (!enabled) {
        writeQueue(emptyQueue());
        dedupe.clear();
    }
};

/* ── Queue persistence ──────────────────────────────────────────────────── */

let memoryQueue = null; // used only when localStorage is unusable

const emptyQueue = () => ({ v: 1, events: [], retryAt: 0, backoff: 0 });

export const readQueue = () => {
    try {
        const s = env.storage();
        if (s) {
            const raw = s.getItem(QUEUE_KEY);
            if (!raw) return memoryQueue ? { ...memoryQueue, events: memoryQueue.events.slice() } : emptyQueue();
            // A corrupted/oversized entry is discarded rather than parsed (Pattern #62).
            if (raw.length > LIMITS.MAX_QUEUE_BYTES * 2) { s.removeItem(QUEUE_KEY); return emptyQueue(); }
            const q = JSON.parse(raw);
            if (!q || !Array.isArray(q.events)) return emptyQueue();
            return { v: 1, events: q.events, retryAt: Number(q.retryAt) || 0, backoff: Number(q.backoff) || 0 };
        }
    } catch (e) { /* fall through */ }
    return memoryQueue ? { ...memoryQueue, events: memoryQueue.events.slice() } : emptyQueue();
};

const priorityOf = (ev) => (HIGH_PRIORITY[ev.category] ? 1 : 0);

/** Enforce count + byte caps, evicting the oldest low-priority event first. */
export const enforceCaps = (queue) => {
    const events = queue.events;
    const size = () => utf8Length(JSON.stringify(queue));
    while (events.length > LIMITS.MAX_QUEUE_EVENTS || (events.length > 0 && size() > LIMITS.MAX_QUEUE_BYTES)) {
        let idx = events.findIndex((e) => priorityOf(e) === 0);
        if (idx < 0) idx = 0;
        events.splice(idx, 1);
    }
    return queue;
};

export const writeQueue = (queue) => {
    enforceCaps(queue);
    const serialized = JSON.stringify(queue);
    try {
        const s = env.storage();
        if (s) {
            if (queue.events.length === 0 && !queue.retryAt) s.removeItem(QUEUE_KEY);
            else s.setItem(QUEUE_KEY, serialized);
            memoryQueue = null;
            return;
        }
    } catch (e) { /* quota / disabled storage → memory */ }
    memoryQueue = queue;
};

const pruneExpired = (queue) => {
    const cutoff = env.now() - LIMITS.EVENT_TTL_MS;
    queue.events = queue.events.filter((e) => typeof e.ts === 'number' && e.ts >= cutoff);
    return queue;
};

/* ── Session id ─────────────────────────────────────────────────────────── */

let memorySessionId = null;
const sessionId = () => {
    try {
        const s = env.session();
        if (s) {
            let id = s.getItem(SESSION_KEY);
            if (!id) { id = generateEventId(); s.setItem(SESSION_KEY, id); }
            return id;
        }
    } catch (e) { /* fall through */ }
    if (!memorySessionId) memorySessionId = generateEventId();
    return memorySessionId;
};

/* ── Sampling / dedupe ──────────────────────────────────────────────────── */

// key -> { windowStart, eventId, suppressed }
const dedupe = new Map();

const dedupeWindowFor = (category) => {
    if (category === 'degraded_continued') return LIMITS.DEGRADED_DEDUPE_WINDOW_MS;
    if (category === 'blocked_explained' || category === 'blocked_silent') return LIMITS.BLOCKED_COALESCE_WINDOW_MS;
    return 0; // false_success / stuck_state: never deduped
};

const dedupeKey = (ev) => [
    ev.category, ev.name, ev.errorName || '', ev.errorCode || '',
    ev.route || '', ev.serverType || '', ev.cosyflowId || '', ev.modelName || '',
    ev.fieldName || '', ev.fieldType || '',
    (ev.detail && ev.detail.path) || '', (ev.detail && ev.detail.action) || '',
    (ev.detail && ev.detail.sourceFile) || '', (ev.detail && ev.detail.sourceLine) || '',
    (ev.detail && ev.detail.swalTitle) || '', (ev.detail && ev.detail.key) || '',
].join('|');

/* ── Public: record an event ────────────────────────────────────────────── */

/**
 * Record one event. Never throws.
 * @param {string} category one of CATEGORIES
 * @param {string} name     event name, e.g. 'blocked_explained', 'stuck_state_timeout'
 * @param {object} fields   top-level contract fields (serverType, cosyflowId, modelName,
 *                          fieldName, fieldType, errorCode, errorName, elapsedMs)
 * @param {object} detail   per-event extras (see DETAIL_KEYS)
 * @returns {string|null}   the eventId queued (or merged into), or null if dropped
 */
export const track = (category, name, fields = {}, detail = null) => {
    try {
        if (!isTelemetryEnabled()) return null;
        if (!CATEGORIES.includes(category)) return null;
        const evName = toIdentifier(name, 128);
        if (!evName) return null;
        const f = fields || {};
        const ev = {
            eventId: generateEventId(),
            ts: env.now(),
            category,
            name: evName,
        };
        // Callers may only suppress the route (null); it is always derived here, never passed in.
        const route = f.route === null ? null : sanitizeRoute();
        if (route) ev.route = route;
        const put = (k, v) => { if (v !== null && v !== undefined && v !== '') ev[k] = v; };
        put('serverType', toIdentifier(f.serverType, 64));
        put('cosyflowId', toIdentifier(f.cosyflowId, 64));
        put('modelName', toModelName(f.modelName));
        put('fieldName', toIdentifier(f.fieldName, 64, LABEL_DISALLOWED));
        put('fieldType', toIdentifier(f.fieldType, 32));
        put('errorCode', toIdentifier(f.errorCode, 64));
        put('errorName', toIdentifier(f.errorName, 128));
        if (typeof f.elapsedMs === 'number' && Number.isFinite(f.elapsedMs)) {
            ev.elapsedMs = Math.max(0, Math.min(2147483647, Math.round(f.elapsedMs)));
        }
        const d = sanitizeDetail(detail);
        if (d) ev.detail = d;
        ev.count = 1;

        if (utf8Length(JSON.stringify(ev)) > LIMITS.MAX_EVENT_BYTES) {
            delete ev.detail;
            if (utf8Length(JSON.stringify(ev)) > LIMITS.MAX_EVENT_BYTES) return null;
        }

        const queue = pruneExpired(readQueue());

        const windowMs = dedupeWindowFor(category);
        if (windowMs > 0) {
            const key = dedupeKey(ev);
            const now = env.now();
            const prev = dedupe.get(key);
            if (prev && now - prev.windowStart < windowMs) {
                const queued = queue.events.find((e) => e.eventId === prev.eventId);
                if (queued) {
                    queued.count = Math.min(10000, (queued.count || 1) + 1);
                    writeQueue(queue);
                } else {
                    prev.suppressed += 1; // already sent — carried on the next event for this key
                }
                return prev.eventId;
            }
            if (prev && prev.suppressed) ev.count = Math.min(10000, 1 + prev.suppressed);
            dedupe.delete(key);
            dedupe.set(key, { windowStart: now, eventId: ev.eventId, suppressed: 0 });
            while (dedupe.size > LIMITS.MAX_DEDUPE_KEYS) dedupe.delete(dedupe.keys().next().value);
        }

        queue.events.push(ev);
        writeQueue(queue);
        scheduleFlush(LIMITS.FLUSH_DELAY_MS);
        return ev.eventId;
    } catch (e) {
        return null;
    }
};

/* ── Global error capture (root_error / global_error / unhandled_rejection) ─ */

// Both src/index.js and App.js listen for the same window events; the SAME Event object
// reaches both, so a WeakSet makes each uncaught error report exactly once.
const reportedEvents = typeof WeakSet === 'function' ? new WeakSet() : null;

const markReported = (obj) => {
    if (!obj || typeof obj !== 'object' || !reportedEvents) return true;
    if (reportedEvents.has(obj)) return false;
    reportedEvents.add(obj);
    return true;
};

/** window 'error' event → degraded_continued/global_error. Message text is never sent. */
export const reportGlobalErrorEvent = (event) => {
    try {
        if (!event || !markReported(event)) return null;
        const err = event.error;
        const errorName = (err && err.name) || (event.message === 'Script error.' ? 'CrossOriginScriptError' : 'Error');
        let frame = {};
        if (event.filename) {
            const file = String(event.filename).split(/[?#]/)[0].split('/').pop();
            frame = {
                sourceFile: toIdentifier(file, 64),
                sourceLine: toIdentifier(`${event.lineno || 0}:${event.colno || 0}`, 32),
            };
        } else if (err && err.stack) {
            frame = frameFromStack(err.stack);
        }
        return track('degraded_continued', 'global_error', { errorName }, frame);
    } catch (e) {
        return null;
    }
};

/** window 'unhandledrejection' event → degraded_continued/unhandled_rejection. */
export const reportUnhandledRejectionEvent = (event) => {
    try {
        if (!event || !markReported(event)) return null;
        const reason = event.reason;
        const errorName = (reason && typeof reason === 'object' && reason.name) || typeof reason;
        const frame = reason && reason.stack ? frameFromStack(reason.stack) : {};
        const code = reason && typeof reason === 'object' ? (reason.code || (reason.response && reason.response.status)) : null;
        return track('degraded_continued', 'unhandled_rejection', { errorName, errorCode: code }, frame);
    } catch (e) {
        return null;
    }
};

/**
 * Pick up errors persisted to localStorage by the global handlers (root_error, global_error,
 * unhandled_rejection) that were written without ever being transported — e.g. by an older
 * build, or on a boot that died before a flush. Each key is ingested at most once per
 * timestamp (PERSISTED_MARK_KEY), and only if within the queue TTL.
 */
export const ingestPersistedErrors = () => {
    try {
        const s = env.storage();
        if (!s || !isTelemetryEnabled()) return 0;
        let seen = {};
        try { seen = JSON.parse(s.getItem(PERSISTED_MARK_KEY) || '{}') || {}; } catch (e) { seen = {}; }
        let ingested = 0;
        const map = { root_error: 'global_error', global_error: 'global_error', unhandled_rejection: 'unhandled_rejection' };
        Object.keys(map).forEach((key) => {
            const raw = s.getItem(key);
            if (!raw || raw.length > 64 * 1024) return;
            let rec;
            try { rec = JSON.parse(raw); } catch (e) { return; }
            const ts = Number(rec && rec.timestamp);
            if (!ts || seen[key] === ts || env.now() - ts > LIMITS.EVENT_TTL_MS) return;
            seen[key] = ts;
            const frame = frameFromStack(rec.stack);
            const m = typeof rec.stack === 'string' ? rec.stack.match(/^([A-Z][A-Za-z]*Error)\b/) : null;
            track('degraded_continued', map[key], { errorName: m ? m[1] : 'Error', route: null }, { ...frame, key });
            ingested++;
        });
        s.setItem(PERSISTED_MARK_KEY, JSON.stringify(seen));
        return ingested;
    } catch (e) {
        return 0;
    }
};

/**
 * Helper for the write sites: call AFTER localStorage.setItem('<key>', ...) so the local
 * copy is not re-ingested on the next boot.
 */
export const markPersistedErrorTransported = (key, timestamp) => {
    try {
        const s = env.storage();
        if (!s) return;
        let seen = {};
        try { seen = JSON.parse(s.getItem(PERSISTED_MARK_KEY) || '{}') || {}; } catch (e) { seen = {}; }
        seen[key] = timestamp;
        s.setItem(PERSISTED_MARK_KEY, JSON.stringify(seen));
    } catch (e) { /* ignore */ }
};

/* ── Transport ──────────────────────────────────────────────────────────── */

/** The signed-in user's own key, or null. Never the legacy anonymous token. */
export const getUserBearer = () => {
    try {
        const ss = env.session();
        const ls = env.storage();
        // API-key ("BytePlus key") accounts: their stored key is a third-party credential,
        // never a Promptus one — do not send it. Covers a restored session too (shared
        // check, one copy — Pattern #99).
        if (isApiKeyAccountSession(ss, ls)) return null;
        // The user switched Promptus cloud calls off (getRequiredAuthToken honours this too).
        if (ss && ss.getItem('promptusApiDisabled') === 'true') return null;
        const key = (ss && ss.getItem('apiKey')) || (ls && ls.getItem('apiKey'));
        if (!key) return null;
        const expiry = (ss && ss.getItem('apiKeyExpiry')) || (ls && ls.getItem('apiKeyExpiry'));
        if (expiry) {
            // Same rule as api-helper isApiKeyExpired (UTC "YYYY-MM-DD HH:MM:SS").
            const d = new Date(`${expiry.replace(' ', 'T')}Z`);
            // The server refuses a key with < 10 min left (401 → batch dropped), so hold the
            // queue instead; it is sent after the key is renewed.
            if (!Number.isNaN(d.getTime()) && env.now() >= d.getTime() - 10 * 60 * 1000) return null;
        }
        return key;
    } catch (e) {
        return null;
    }
};

const endpointUrl = () => {
    if (env.apiBase) return `${env.apiBase}/api/client-errors`;
    let dev = false;
    try {
        const ss = env.session();
        dev = !!ss && ss.getItem('use_dev_api_cache') === 'true';
    } catch (e) { /* default prod */ }
    return `${dev ? 'https://api.dev.promptus.ai' : 'https://api.promptus.ai'}/api/client-errors`;
};

const wireEvent = (e) => {
    const out = {};
    Object.keys(e).forEach((k) => { if (k.charAt(0) !== '_') out[k] = e[k]; });
    return out;
};

/** Build the next request body from the queue: ≤ 50 events and ≤ MAX_BODY_BYTES. */
export const buildBatch = (queue) => {
    const header = { clientVersion: clientVersion(), platform: detectPlatform(), sessionId: sessionId() };
    const events = queue.events.slice(0, LIMITS.MAX_EVENTS_PER_REQUEST).map(wireEvent);
    let body = JSON.stringify({ ...header, events });
    while (events.length > 1 && utf8Length(body) > LIMITS.MAX_BODY_BYTES) {
        events.pop();
        body = JSON.stringify({ ...header, events });
    }
    if (events.length === 0 || utf8Length(body) > LIMITS.MAX_BODY_BYTES) return null;
    return { body, ids: events.map((e) => e.eventId) };
};

const parseRetryAfter = (value) => {
    if (!value) return 0;
    const secs = Number(value);
    if (Number.isFinite(secs)) return Math.max(0, secs * 1000);
    const date = Date.parse(value);
    return Number.isNaN(date) ? 0 : Math.max(0, date - env.now());
};

const backoffDelay = (step) => {
    const exp = Math.min(LIMITS.BACKOFF_MAX_MS, LIMITS.BACKOFF_BASE_MS * Math.pow(2, Math.max(0, step - 1)));
    return Math.round(exp * (0.8 + 0.4 * env.random()));
};

let inFlight = false;
let flushTimer = null;

export const scheduleFlush = (delayMs) => {
    try {
        if (flushTimer) return;
        flushTimer = env.setTimeout(() => {
            flushTimer = null;
            flush();
        }, delayMs);
    } catch (e) { /* ignore */ }
};

/**
 * Send one batch. Resolves to a short status string (for tests/diagnostics). Never throws.
 * @param {{keepalive?: boolean}} opts keepalive=true for pagehide/visibility flushes
 */
export const flush = async ({ keepalive = false } = {}) => {
    try {
        if (!isTelemetryEnabled()) return 'disabled';
        if (inFlight) return 'in_flight';
        let queue = pruneExpired(readQueue());
        if (queue.events.length === 0) { writeQueue(queue); return 'empty'; }
        if (env.now() < queue.retryAt) return 'backoff';
        const token = getUserBearer();
        if (!token) { writeQueue(queue); return 'no_auth'; } // keep; ages out via TTL
        const batch = buildBatch(queue);
        if (!batch) {
            // A single event bigger than the body budget can never be sent — drop it.
            queue.events.shift();
            writeQueue(queue);
            return 'oversize_dropped';
        }

        inFlight = true;
        let status = 0;
        let retryAfter = null;
        try {
            const res = await env.fetch(endpointUrl(), {
                method: 'POST',
                keepalive,
                credentials: 'omit',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: batch.body,
            });
            status = res ? res.status : 0;
            retryAfter = res && res.headers && typeof res.headers.get === 'function' ? res.headers.get('Retry-After') : null;
        } catch (e) {
            status = 0; // network failure
        } finally {
            inFlight = false;
        }

        // Re-read: track() may have appended while the request was in flight.
        queue = pruneExpired(readQueue());
        const sent = new Set(batch.ids);

        if (status >= 200 && status < 300) {
            queue.events = queue.events.filter((e) => !sent.has(e.eventId));
            queue.retryAt = 0;
            queue.backoff = 0;
            writeQueue(queue);
            if (queue.events.length > 0) scheduleFlush(1000);
            return 'sent';
        }
        if (status === 400 || status === 401 || status === 403 || status === 413) {
            queue.events = queue.events.filter((e) => !sent.has(e.eventId));
            writeQueue(queue);
            return 'dropped';
        }

        // Retryable: 429 / 503 / network / anything else (e.g. 404 before deploy).
        queue.backoff = Math.min(20, (queue.backoff || 0) + 1);
        let delay = backoffDelay(queue.backoff);
        if (status === 429 || status === 503) delay = Math.max(delay, parseRetryAfter(retryAfter));
        queue.retryAt = env.now() + delay;
        if (status !== 429 && status !== 404) {
            // 429 (rate limit) and 404 (endpoint not deployed yet) are not the events' fault;
            // they are bounded by the TTL and queue caps instead. Everything else counts
            // toward the attempt cap.
            queue.events = queue.events
                .map((e) => (sent.has(e.eventId) ? { ...e, _a: (e._a || 0) + 1 } : e))
                .filter((e) => (e._a || 0) < LIMITS.MAX_SEND_ATTEMPTS);
        }
        writeQueue(queue);
        scheduleFlush(delay + 50);
        return status === 0 ? 'network_retry' : `retry_${status}`;
    } catch (e) {
        inFlight = false;
        return 'error';
    }
};

/* ── Boot wiring ────────────────────────────────────────────────────────── */

let initialized = false;
let intervalId = null;

/**
 * Idempotent. Called from src/index.js at module scope — before, and independent of, the
 * safe-mode branch — so queued events are delivered even when the app itself cannot boot.
 */
export const initClientTelemetry = () => {
    if (initialized || typeof window === 'undefined' || typeof document === 'undefined') return;
    initialized = true;
    try {
        const onHide = () => { flush({ keepalive: true }); };
        window.addEventListener('pagehide', onHide);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') onHide();
        });
        intervalId = setInterval(() => { flush(); }, LIMITS.FLUSH_INTERVAL_MS);
        // Let the app boot before the first send.
        env.setTimeout(() => {
            ingestPersistedErrors();
            flush();
        }, 10 * 1000);
    } catch (e) { /* telemetry must never break boot */ }
};

/** Test-only: reset module state. */
export const __resetTelemetryForTests = () => {
    dedupe.clear();
    memoryQueue = null;
    memoryOptOut = false;
    memorySessionId = null;
    inFlight = false;
    if (flushTimer) { try { env.clearTimeout(flushTimer); } catch (e) { /* ignore */ } }
    flushTimer = null;
    if (intervalId) clearInterval(intervalId);
    intervalId = null;
    initialized = false;
};
